import { useState, type ReactNode } from 'react';

export interface ComparisonBarChartItem {
  name: string;
  primaryScore: number;
  secondaryScore: number;
  primaryColor: string;
  /** Optional third series (e.g. an overall/benchmark score) rendered below the secondary bar. */
  tertiaryScore?: number;
}

export interface ComparisonBarChartProps {
  title: string;
  items: ComparisonBarChartItem[];
  primaryLabel: string;
  secondaryLabel: string;
  secondaryColor?: string;
  tertiaryLabel?: string;
  tertiaryColor?: string;
  max?: number;
  steps?: number[];
  isLoading?: boolean;
  headerRight?: ReactNode;
}

export function ComparisonBarChart({
  title,
  items,
  primaryLabel,
  secondaryLabel,
  secondaryColor = '#64748b',
  tertiaryLabel,
  tertiaryColor = '#94a3b8',
  max = 100,
  steps = [0, 25, 50, 75, 100],
  isLoading,
  headerRight,
}: ComparisonBarChartProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const hasTertiary = tertiaryLabel !== undefined;

  if (isLoading && items.length === 0) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border backdrop-blur-sm">
        <div className="border-(--color-border) flex items-center justify-between gap-3 border-b px-6 py-4">
          <p className="text-(--color-accent) text-sm font-bold">{title}</p>
          {headerRight}
        </div>
        <div className="space-y-4 px-6 py-8">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) flex items-center justify-between gap-3 border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">{title}</p>
        {headerRight}
      </div>

      <div className="flex items-center gap-6 px-6 pt-4">
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5">
            {[...new Set(items.map((d) => d.primaryColor))].map((color) => (
              <span
                key={color}
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <span className="text-(--color-text-secondary) text-xs">
            {primaryLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-5 rounded-sm"
            style={{ backgroundColor: secondaryColor }}
          />
          <span className="text-(--color-text-secondary) text-xs">
            {secondaryLabel}
          </span>
        </div>
        {hasTertiary && (
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-5 rounded-sm"
              style={{ backgroundColor: tertiaryColor }}
            />
            <span className="text-(--color-text-secondary) text-xs">
              {tertiaryLabel}
            </span>
          </div>
        )}
      </div>

      <div className="px-6 py-5">
        <div className="flex gap-4">
          <div className="w-32.5 sm:w-42.5 flex shrink-0 flex-col">
            {/* Spacer matching the grid-step label row on the right so each
                label lines up with its bars. */}
            <div className="mb-2 h-4" />
            <div className="space-y-1">
              {items.map((d) => (
                <div
                  key={d.name}
                  className={`${hasTertiary ? 'h-18' : 'h-12.5'} flex items-center justify-end`}
                >
                  <span className="text-(--color-text-secondary) text-right text-xs leading-tight">
                    {d.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative mr-14 min-w-0 flex-1">
            <div className="relative mb-2 flex h-4">
              {steps.map((step) => (
                <span
                  key={step}
                  className="text-(--color-text-secondary) absolute -translate-x-1/2 text-xs opacity-60"
                  style={{ left: `${(step / max) * 100}%` }}
                >
                  {step}
                </span>
              ))}
            </div>

            <div className="relative">
              <div className="pointer-events-none absolute inset-0">
                {steps.map((step) => (
                  <div
                    key={step}
                    className="border-white/8 absolute top-0 h-full border-l"
                    style={{ left: `${(step / max) * 100}%` }}
                  />
                ))}
              </div>

              <div className="space-y-1">
                {items.map((d) => (
                  <div
                    key={d.name}
                    className="relative space-y-1.5 py-1.5"
                    onMouseEnter={() => setHoveredItem(d.name)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {hoveredItem === d.name && (
                      <div className="border-(--color-border) bg-(--color-bg-secondary) pointer-events-none absolute left-0 top-full z-20 mt-1 rounded-lg border px-3 py-2 shadow-lg">
                        <p
                          className="text-xs font-semibold"
                          style={{ color: d.primaryColor }}
                        >
                          {d.name}
                        </p>
                        <p className="text-(--color-text-secondary) text-xs">
                          {primaryLabel}:{' '}
                          <span
                            className="font-bold"
                            style={{ color: d.primaryColor }}
                          >
                            {d.primaryScore.toFixed(2)}%
                          </span>
                          {'  ·  '}
                          {secondaryLabel}:{' '}
                          <span
                            className="font-bold"
                            style={{ color: secondaryColor }}
                          >
                            {d.secondaryScore.toFixed(2)}%
                          </span>
                          {hasTertiary && d.tertiaryScore !== undefined && (
                            <>
                              {'  ·  '}
                              {tertiaryLabel}:{' '}
                              <span
                                className="font-bold"
                                style={{ color: tertiaryColor }}
                              >
                                {d.tertiaryScore.toFixed(2)}%
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    )}
                    <div className="relative h-4">
                      <div
                        className="absolute left-0 top-0 h-full rounded-sm"
                        style={{
                          width: `${(d.primaryScore / max) * 100}%`,
                          backgroundColor: d.primaryColor,
                        }}
                      />
                      <span
                        className="absolute top-1/2 -translate-y-1/2 pl-1.5 text-xs font-bold tabular-nums"
                        style={{
                          left: `${(d.primaryScore / max) * 100}%`,
                          color: d.primaryColor,
                        }}
                      >
                        {d.primaryScore.toFixed(2)}%
                      </span>
                    </div>
                    <div className="relative h-4">
                      <div
                        className="absolute left-0 top-0 h-full rounded-sm"
                        style={{
                          width: `${(d.secondaryScore / max) * 100}%`,
                          backgroundColor: secondaryColor,
                        }}
                      />
                      <span
                        className="absolute top-1/2 -translate-y-1/2 pl-1.5 text-xs font-bold tabular-nums"
                        style={{
                          left: `${(d.secondaryScore / max) * 100}%`,
                          color: secondaryColor,
                        }}
                      >
                        {d.secondaryScore.toFixed(2)}%
                      </span>
                    </div>
                    {hasTertiary && d.tertiaryScore !== undefined && (
                      <div className="relative h-4">
                        <div
                          className="absolute left-0 top-0 h-full rounded-sm"
                          style={{
                            width: `${(d.tertiaryScore / max) * 100}%`,
                            backgroundColor: tertiaryColor,
                          }}
                        />
                        <span
                          className="absolute top-1/2 -translate-y-1/2 pl-1.5 text-xs font-bold tabular-nums"
                          style={{
                            left: `${(d.tertiaryScore / max) * 100}%`,
                            color: tertiaryColor,
                          }}
                        >
                          {d.tertiaryScore.toFixed(2)}%
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
